import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchHistoryPrescriptionSurgery } from '@/services/medicalHistoryService';
import { postMedicalHistory } from '@/services/medicalHistoryService';
import { fetchPersonalData } from '@/services/personalService';
import { fetchAllDoctor } from '@/services/doctorService';
import { setDoctorsRedux } from '@/redux/actions/doctorActions';
import emailjs from '@emailjs/browser';
import { getCurrentUserId } from '@/utils/getCurrentUserId';

export const useDatingHistory = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const [patientsxUser, setPatientsxUser] = useState([]);
  const doctors = useSelector(state => state.doctors);
  const loadPersonalMutexRef = useRef(false);

  const doctor = doctors?.find(d => Number(d.codigo) === 2);

  const loadHistoryByPatient = async paciente => {
    try {
      const payload = {
        num: 10,
        cod_paciente: paciente,
        cod_medico: null,
      };
      return await fetchHistoryPrescriptionSurgery(payload);
    } catch (err) {
      console.error(` Error history paciente ${paciente}:`, err);
      return null;
    }
  };

  const loadDatingHistory = async (currentPatients = null) => {
    try {
      setError(null);
      const authData = JSON.parse(localStorage.getItem('auth_data'));
      const mainCodPaciente = authData?.paciente?.codigo_de_paciente;

      if (!mainCodPaciente) {
        throw new Error('No se encontró código de paciente principal');
      }

      const patientsToList = currentPatients || patientsxUser;

      const mainHistoryPromise = loadHistoryByPatient(mainCodPaciente);

      const associatedPromises = patientsToList.map(async paciente => {
        const history = await loadHistoryByPatient(paciente.codigo_de_paciente);
        if (history?.historia_medica?.data) {
          const data = history.historia_medica.data;
          const items = Array.isArray(data) ? data : [data];
          return items.map(item => ({ ...item, paciente }));
        }
        return [];
      });

      const [mainHistory, ...associatedResults] = await Promise.all([
        mainHistoryPromise,
        ...associatedPromises,
      ]);

      const associatedHistories = associatedResults.flat();
      if (mainHistory?.historia_medica?.data) {
        const mainData = mainHistory.historia_medica.data;
        const mainItems = Array.isArray(mainData) ? mainData : [mainData];
        const mainWithPatient = mainItems.map(item => ({
          ...item,
          paciente: authData?.paciente,
        }));
        associatedHistories.push(...mainWithPatient);
      }

      console.log('REPORT DATA FINAL:', associatedHistories);
      associatedHistories.sort((a, b) => {
        return (b.fecha_de_atencion || '').localeCompare(
          a.fecha_de_atencion || '',
        );
      });
      setReportData(associatedHistories);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error cargando historial general');
    }
  };

  const loadPersonalByDoctor = async () => {
    if (loadPersonalMutexRef.current) return;
    loadPersonalMutexRef.current = true;
    setIsLoading(true);

    try {
      if (!doctor) {
        console.warn('No existe doctor con código 2');
        return;
      }

      if (!doctor?.numero_de_dni || !doctor?.codigo_personal) {
        return;
      }

      const userId = getCurrentUserId();

      if (!userId) throw new Error('No se pudo obtener el usuario logueado');

      const response = await fetchPersonalData({
        numero_de_dni: doctor.numero_de_dni,
      });

      const pacientesRaw = Array.isArray(response?.pacientes)
        ? response.pacientes
        : [];
      const pacientesAsociados = pacientesRaw.filter(p => {
        if (p?.user_id == null || p.user_id === '') return false;
        return Number(p.user_id) === Number(userId);
      });

      if (pacientesRaw.length > 0 && pacientesAsociados.length === 0) {
        console.warn(
          '[DatingHistory] Personal devolvió pacientes pero ninguno coincide con userId',
          {
            userId,
            sampleUserIds: pacientesRaw.slice(0, 3).map(p => p.user_id),
          },
        );
      }

      setPatientsxUser(pacientesAsociados);
      await loadDatingHistory(pacientesAsociados);
    } catch (err) {
      console.error('Error en loadPersonalByDoctor:', err);
    } finally {
      loadPersonalMutexRef.current = false;
      setIsLoading(false);
    }
  };

  const saveMedicalHistory = async (reportDataParam, paciente) => {
    try {
      // Payload alineado con HistoriaMedicaController@store: solo si
      // pacientes_codigo, proxima_cita, numero_de_historia_clinica son null
      // y estado_de_horario === 'LIBRE', se inserta en anulaciones_cancelaciones.
      const usuarioId = getCurrentUserId();

      await postMedicalHistory({
        codigo: reportDataParam?.codigo,
        numero_de_historia_clinica: null,
        motivo: 'Anulación desde sistema',
        servicio:
          reportDataParam?.tipo_de_consulta?.titulo_de_tipo || 'Consulta',
        pacientes_codigo: null,
        proxima_cita: null,
        personal_codigo: reportDataParam?.personal?.codigo_personal,
        tipo_de_consulta: null,
        monto_cobrado: null,
        costo_de_consulta: null,
        tipo_de_estado_codigo: null,
        estado_de_horario: 'LIBRE',
        fecha_de_atencion: reportDataParam?.fecha_de_atencion,
        hora_de_atencion: reportDataParam?.hora_de_atencion,
        fecha_de_registro: reportDataParam?.fecha_de_registro,
        hora_de_registro: reportDataParam?.hora_de_registro,
        tipo_de_reserva: null,
        duracion_de_atencion: reportDataParam?.duracion_de_atencion,
        fecha_de_reserva_atc: null,
        hora_de_reserva_atc: null,
        ...(usuarioId != null
          ? {
              ID_USUARIO: usuarioId,
              TEXT_USUARIO:
                [paciente?.nombres, paciente?.apellido_paterno]
                  .filter(Boolean)
                  .join(' ')
                  .trim() || String(usuarioId),
            }
          : {}),
      });

      const templateParams = {
        user_name: 'Usuario',
        reply_to: paciente?.correo_electronico,
        copy_to: paciente?.correo_electronico,
        message:
          'Estimado paciente ' +
          paciente?.nombres +
          ' ' +
          paciente?.apellido_paterno +
          ', su cita fué anulada correctamente',
      };

      emailjs
        .send(
          'service_zdu3k6p',
          'template_3zc9p4j',
          templateParams,
          'SuYqnE4z3_IEWAh90',
        )
        .then(
          response => {
            console.log('ÉXITO!', response.status, response.text);
          },
          err => {
            console.log('FALLÓ...', err);
          },
        );

      await loadPersonalByDoctor();
    } catch (err) {
      console.error('Error guardando historia:', err);
    }
  };

  useEffect(() => {
    if (doctor) {
      loadPersonalByDoctor();
      return;
    }

    // Store de doctores vacío: lo hidratamos para evitar spinner infinito
    // cuando se entra directo a /historial sin pasar por Home.
    if (!doctors || doctors.length === 0) {
      fetchAllDoctor()
        .then(response => {
          const medicosActivosOrdenados =
            response?.medicos
              ?.filter(
                m => String(m.est_personal) === '1' && m.orden_externo !== null,
              )
              ?.sort((a, b) => {
                const ordenA = Number(a.orden_externo) || 0;
                const ordenB = Number(b.orden_externo) || 0;
                return ordenA - ordenB;
              }) || [];
          dispatch(setDoctorsRedux(medicosActivosOrdenados));
        })
        .catch(err => {
          console.error('Error cargando médicos:', err);
          setError(err.message || 'Error cargando médicos');
          setIsLoading(false);
        });
      return;
    }

    // Lista cargada pero no existe el doctor con código 2:
    // soltamos el spinner para no dejar la pantalla colgada.
    setIsLoading(false);
  }, [doctor, doctors]);

  return {
    isLoading,
    reportData,
    loadDatingHistory: () => loadDatingHistory(patientsxUser),
    error,
    saveMedicalHistory,
  };
};
